import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";
import { Building2 } from "lucide-react";

export default function Login() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); toast.success("Signed in"); navigate("/dashboard"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8FAF9]">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white">
        <div className="flex items-center gap-2">
          <Brand variant="light" size={24} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-3">Housing Opportunity Infrastructure</div>
          <h1 className="text-4xl font-bold leading-tight mb-4">Connect fragmented housing demand to vetted real estate.</h1>
          <p className="text-slate-300 text-sm leading-relaxed max-w-md">
            Intelligence, CRM, bidirectional matching, dual opportunity/confidence scoring, and investor-grade information packets
            — for Greater San Antonio: Bexar, Comal, Guadalupe, Kendall.
          </p>
        </div>
        <div className="text-[11px] text-slate-500">Not investment advice · Evidence-forward · Greater San Antonio</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="mb-6 flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest font-semibold">
            <Building2 className="w-4 h-4" /> Operations Portal
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-1">Welcome back</h2>
          <p className="text-sm text-slate-500 mb-6">Sign in to your operations workspace.</p>

          <form onSubmit={submitLogin} className="space-y-3" data-testid="login-form">
            <div><Label>Email</Label><Input data-testid="login-email" value={email} onChange={e=>setEmail(e.target.value)} type="email" required /></div>
            <div><Label>Password</Label><Input data-testid="login-password" value={password} onChange={e=>setPassword(e.target.value)} type="password" required /></div>
            <Button type="submit" data-testid="login-submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800">Sign in</Button>
            <div className="text-right"><Link to="/forgot-password" className="text-xs text-slate-600 underline hover:text-slate-900" data-testid="login-forgot-link">Forgot your password?</Link></div>
          </form>

          <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-400">
            <div className="h-px flex-1 bg-slate-200" /> or <div className="h-px flex-1 bg-slate-200" />
          </div>
          <Button variant="outline" className="w-full" onClick={googleLogin} data-testid="google-login-btn">
            Continue with Google (Emergent)
          </Button>

          <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-500 leading-relaxed" data-testid="login-request-access-note">
            Throughline is an internal operations workspace. Public registration is closed —
            new accounts are added by invitation after review. If you need access, please <Link to="/#request-access" className="text-slate-700 underline hover:text-slate-900" data-testid="login-request-access-link">request it here</Link>.
          </div>
        </div>
      </div>
    </div>
  );
}
